import { Box, Typography, Chip, useTheme, IconButton } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from 'components/Common/DataTable';
import { MdOutlineFileDownload, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { BillingHistoryResponse } from '@/apis/agency/billing/types';
import { useGetAgencyBillingHistories, useFetchAgencyInvoice } from '@/apis/agency/billing/hook';
import { formatDollars } from '@/utils/helperFunctions';
import { useMemo } from 'react';
import BillingHistoryRowDetails from '@/components/Common/BillingHistoryRowDetails';

export default function BillingHistory() {
  const theme = useTheme();
  const { data: BillingHistoryData, isLoading } = useGetAgencyBillingHistories();
  const memoizedBillingHistory = useMemo(() => BillingHistoryData?.data ?? [], [BillingHistoryData?.data]);
  const { mutate: fetchInvoice, isPending: isFetchingInvoice, variables: fetchingInvoiceId } = useFetchAgencyInvoice();

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
        Billing History
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        mb={2}
        mt={0.5}
        gutterBottom
        sx={{ fontSize: { xs: '14px', lg: 16 } }}
      >
        Monthly usage & billing details
      </Typography>
      <DataTable
        isLoading={isLoading}
        data={memoizedBillingHistory}
        columns={columns(theme, fetchInvoice, isFetchingInvoice, fetchingInvoiceId)}
        renderAfterRow={(row) => (
          <BillingHistoryRowDetails invoiceId={row.original.invoice_id ?? row.original.id} isBusinessView={false} />
        )}
      />
    </Box>
  );
}

export const columns = (
  theme: any,
  fetchInvoice: (id: number) => void,
  isFetchingInvoice: boolean,
  fetchingInvoiceId?: number
): ColumnDef<BillingHistoryResponse>[] => {
  const getStatus = (status: string) => {
    switch (status) {
      case 'pending':
      case 'draft':
        return { color: '#777777', bgcolor: '#ECECEC', label: 'Draft' };
      case 'finalized':
      case 'failed':
        return { color: '#7C00B1', bgcolor: '#F5DFFF', label: 'Open' };
      case 'succeeded':
        return { color: '#106520ff', bgcolor: '#bae7ccff', label: 'Paid' };
      case 'voided':
        return { color: '#FB8703', bgcolor: '#FFF3E5', label: 'Voided' };
      case 'refunded':
        return { color: '#B36B00', bgcolor: '#FFF3DF', label: 'Refunded' };
      default:
        return { color: theme.palette.primary.dark, bgcolor: theme.palette.primary.light, label: status };
    }
  };

  return [
    {
      header: 'Month',
      accessorKey: 'month',
      cell: ({ row }) => (
        <Box display='flex' alignItems='center' gap={0.5}>
          <IconButton size='small' onClick={row.getToggleExpandedHandler()} sx={{ p: 0.25 }}>
            {row.getIsExpanded() ? <MdKeyboardArrowUp size={18} /> : <MdKeyboardArrowDown size={18} />}
          </IconButton>
          <Typography>{row.original.month}</Typography>
        </Box>
      )
    },
    {
      accessorKey: 'subtotal',
      header: 'Subtotal',
      cell: (info) => formatDollars(info?.row.original?.subtotal)
    },
    {
      accessorKey: 'tax_amount',
      header: 'Tax',
      cell: (info) => formatDollars(info?.row.original?.tax_amount)
    },
    {
      accessorKey: 'discount_amount',
      header: 'Discount',
      cell: (info) => (
        <Typography fontWeight={500} color='#2E7D32'>
          {formatDollars(info?.row?.original?.discount_amount)}
        </Typography>
      )
    },
    {
      accessorKey: 'total',
      header: 'Total(Incl. Tax)',
      cell: (info) => formatDollars(info?.row.original?.total)
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) =>
        row?.original?.status ? (
          <Chip
            label={getStatus(row.original.status).label}
            size='small'
            sx={{
              color: getStatus(row.original.status).color,
              bgcolor: getStatus(row.original.status).bgcolor,
              p: { xs: 1, lg: 2 },
              borderRadius: 6,
              fontWeight: 500,
              fontSize: { xs: '0.7rem', lg: '0.75rem' },
              height: { xs: 24, lg: 32 }
            }}
          />
        ) : (
          ''
        )
    },
    {
      id: 'invoice',
      header: 'Invoice',
      cell: (info) => {
        const { id, status } = info.row.original;
        const isRowFetching = isFetchingInvoice && fetchingInvoiceId === id;
        return ['finalized', 'failed', 'voided', 'succeeded', 'refunded'].includes(status) ? (
          <IconButton color='primary' disabled={!id || isRowFetching} onClick={() => id && fetchInvoice(id)}>
            <MdOutlineFileDownload />
          </IconButton>
        ) : (
          ''
        );
      }
    }
  ];
};
